
// src/components/usermanagement/UserModal.jsx
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
} from 'antd';
import { useAddUser, useEmailExists, useUpdateUserDetails } from '../../hooks/userManagement';
import { recordError } from '../../utils/logService';

const { Option } = Select;

/**
 * UserModal – unified modal for Add / Edit / View.
 *
 * @param {Object}   props
 * @param {boolean}  props.show
 * @param {Function} props.onShow
 * @param {Array<{key:string,value:string}>} props.roles
 * @param {Function} props.setHasUpdated
 * @param {Object=}  props.record
 * @param {boolean=} props.viewOnly
 * @returns {JSX.Element}
 */
export const UserModal = ({
  show,
  onShow,
  roles,
  setHasUpdated,
  record,
  viewOnly,
}) => {
  const [form] = Form.useForm();
  const emailAddressRef = useRef(null);
  const [disableCancel, setDisableCancel] = useState(true);

  const isEdit = !!record && !viewOnly;

  const { fetchEmailStatus } = useEmailExists();
  const { mutateAsync: addUser } = useAddUser();
  const { mutateAsync: updateUser } = useUpdateUserDetails();

  useEffect(() => {
    if (show) {
      if (record) {
        const { id, emailAddress, firstname, lastname, roles: rls, enabled } = record; // roles are KEYS
        form.setFieldsValue({ id, emailAddress, firstname, lastname, roles: rls, enabled });
      } else {
        form.resetFields();
      }
      setDisableCancel(true);
    }
  }, [show, record, viewOnly]);

  const checkEmailExistance = async (e) => {
    if (isEdit || viewOnly) return;
    const value = e?.target?.value?.trim();
    if (!value) return;

    try {
      await fetchEmailStatus(value);
      form.setFields([{ name: 'emailAddress', errors: [] }]);
    } catch (error) {
      emailAddressRef.current?.focus();
      const msg = error?.response?.status === 409 ? 'Email already exists' : 'Invalid email domain';
      form.setFields([{ name: 'emailAddress', errors: [msg] }]);
    }
  };

  const onSubmit = async (values) => {
    if (viewOnly) return;

    // Ensure roles payload is KEYS only
    values.roles = Array.isArray(values.roles)
      ? values.roles.map((r) => (typeof r === 'string' ? r : r?.key ?? r?.value ?? String(r)))
      : [];

    try {
      if (isEdit) await updateUser(values);
      else await addUser(values);
      setHasUpdated(true);
      handleClose();
    } catch (error) {
      recordError(error);
    }
  };

  const handleValuesChange = async () => {
    setDisableCancel(!form.isFieldsTouched(true));
  };

  const handleClose = () => {
    form.resetFields();
    onShow(false);
    setDisableCancel(true);
  };

  const handleCancel = () => {
    if (!form.isFieldsTouched(true)) handleClose();
    else setDisableCancel(false);
  };

  const getTitle = () => (viewOnly ? 'View User Details' : isEdit ? 'Edit User Details' : 'Add New User');

  return (
    <Modal
        key="modal-edit-user"
        forceRender
        title={getTitle()}
        open={show}
        closable={false}
        keyboard={false}
        maskClosable={false}
        footer={[
          <div
            className="modal-footer-buttons"
            key="modal-footer"
          >
            {!viewOnly && (
              <>
                <Button
                  type="primary"
                  htmlType="submit"
                  form="UserForm"
                >
                  Save
                </Button>

                <Popconfirm
                  title="Are you sure you want to cancel...you will lose all info entered?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={handleClose}
                  onCancel={handleCancel}
                  disabled={disableCancel}
                >
                  <Button
                    onMouseUp={handleCancel}
                    style={{ marginLeft: 10 }}
                  >
                    Cancel
                  </Button>
                </Popconfirm>
              </>
            )}

            {viewOnly && (
              <Button
                onMouseUp={handleClose}
                style={{ marginLeft: 10 }}
              >
                Cancel
              </Button>
            )}
          </div>,
        ]}
      >
        <Form
          name="UserForm"
          form={form}
          onFinish={onSubmit}
          disabled={viewOnly}
          onValuesChange={handleValuesChange}
        >
          {isEdit && (
            <Form.Item
              hidden
              name="id"
              label="id"
            >
              <Input name="id" />
            </Form.Item>
          )}

          <Form.Item
            name="emailAddress"
            label="Username"
            validateTrigger="onBlur"
            onBlur={checkEmailExistance}
            rules={[
              { type: 'email', message: 'The input is not valid E-mail' },
              { required: true, message: 'Please enter your E-mail' },
            ]}
          >
            <Input
              disabled={isEdit || viewOnly}
              ref={emailAddressRef}
              name="emailAddress"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="firstname"
            label="First name"
            rules={[{ required: true, message: 'Please enter your firstname' }]}
          >
            <Input
              name="firstname"
              autoComplete="given-name"
            />
          </Form.Item>

          <Form.Item
            name="lastname"
            label="Last name"
            rules={[{ required: true, message: 'Please enter your lastname' }]}
          >
            <Input
              name="lastname"
              autoComplete="family-name"
            />
          </Form.Item>

          <Form.Item
            name="roles"
            label="User roles"
            rules={[{ required: true, message: 'Please select user role (s)' }]}
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Please select"
            >
              {(roles || []).map((role) => (
                <Option
                  key={`${role.key ?? role.value}__${role.value}`}
                  value={role.key}
                >
                  {role.value}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {(isEdit || viewOnly) && (
            <Form.Item
              valuePropName="checked"
              name="enabled"
              label="Is Active?"
            >
              <Checkbox name="enabled" />
            </Form.Item>
          )}
        </Form>
      </Modal>
  );
}

UserModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onShow: PropTypes.func.isRequired,
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  setHasUpdated: PropTypes.func.isRequired,
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    emailAddress: PropTypes.string,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    roles: PropTypes.arrayOf(PropTypes.string), // KEYS
    enabled: PropTypes.bool,
    emailVerified: PropTypes.bool,
  }),
  viewOnly: PropTypes.bool,
};